import { useContext } from 'react';
import { useSelector } from 'react-redux';

import { DealsExperiment, type TDealsExperimentGroup } from '../../../constants/dealsExperiment';
import { selectCpdRegions } from '../../../selectors/cpdRegions';
import type { TDealsBannerConfig } from '../../../types/dealsBannerConfig';
import type { TDealsContentConfig } from '../../../types/dealsContentConfig';
import type { TPostState } from '../../../types/post';
import { ERequestStatus } from '../../../types/requestStatus';
import { ApplicationContext } from '../../../utils';
import {
  getDealsWidgetsConfig,
  isDealsExperimentEligible,
  shouldEnableDealsBannerInContent,
  shouldShowDealsWidgets,
} from '../../../utils/dealsExperiment';
import { useDealsWidgetsProps, type IDealsExperimentWidgets } from './useDealsWidgetsProps';

export interface IUseDealsExperimentResult {
  enableDealsBannerInContent: boolean;
  widgets: IDealsExperimentWidgets | null;
  isEligible: boolean;
  group: TDealsExperimentGroup;
}

/**
 * Собирает решение эксперимента Сделки для статьи: показывать ли виджеты,
 * пропсы формы/баннера, нужно ли в пайплайне контента парсить маркер
 * баннера и глушить конкурирующие вставки, и входит ли статья в выборку
 * (группы A и B) для событий глубины чтения.
 */
export const useDealsExperiment = (post: TPostState): IUseDealsExperimentResult => {
  const group = DealsExperiment.use();
  const { items: cpdRegionOptions } = useSelector(selectCpdRegions);
  const {
    config,
    custom: { isDealsBannerExperimentEnabled },
  } = useContext(ApplicationContext);
  const isGroupB = shouldShowDealsWidgets(group);
  /**
   * Пока статья грузится, в сторе ещё лежит предыдущая — смотрим на status,
   * иначе по её id решим, что сделка нужна и на новой странице.
   */
  const isPostReady = post.status === ERequestStatus.Succeed;
  const postId = isPostReady ? post.id : undefined;
  const dealsContentConfig = config.get<TDealsContentConfig>('frontend-content.dealsContentConfig');
  const dealsBannerConfig = config.get<TDealsBannerConfig>('frontend-content.dealsBannerConfig');
  const widgetsConfig =
    postId !== undefined ? getDealsWidgetsConfig(postId, dealsContentConfig, dealsBannerConfig) : {};
  const widgets = useDealsWidgetsProps(postId ?? 0, isDealsBannerExperimentEnabled, cpdRegionOptions, widgetsConfig);
  const enableDealsBannerInContent =
    isPostReady &&
    shouldEnableDealsBannerInContent({
      isFeatureEnabled: isDealsBannerExperimentEnabled,
      isGroupB,
      hasBanner: Boolean(widgetsConfig.banner),
    });
  const isEligible =
    postId !== undefined && isDealsExperimentEligible(isDealsBannerExperimentEnabled, postId, dealsContentConfig);

  if (!isPostReady || !isGroupB) {
    return { widgets: null, enableDealsBannerInContent, isEligible, group };
  }

  return {
    widgets: widgets ?? null,
    enableDealsBannerInContent,
    isEligible,
    group,
  };
};
